import Button from '@pelckmans/elementis/components/button';
import ButtonGroup from '@pelckmans/elementis/components/button-group';
import { useToast } from '@pelckmans/elementis/components/toast';
import CloseIcon from '@pelckmans/elementis/icons/close';
import TransferIcon from '@pelckmans/elementis/icons/swap';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { TOAST_ID } from '../../../../constants/ToastIds';
import IdentityContext from '../../../../contexts/IdentityContext';
import ModalContext from '../../../../contexts/ModalContext';
import { buildGroupProps } from '../../../../utils/elementisUtils';
import { cancelLicenseTransfer, License, LICENSE_TRANSFER_DIRECTION, LicenseTransfer } from '../../api/licenses';
import { LicenseTransferModal } from './LicenseTransferModal';
import { getLicenseState } from './Pills';

const BASE = 'PAGES.PROFILE.LICENSES.MY_LICENSES.ACTIONS';

export default function LicenseActions({
  license,
  licenseTransfers,
  refetchLicenseTransfers,
}: {
  license: License;
  licenseTransfers: LicenseTransfer[];
  refetchLicenseTransfers: () => void;
}) {
  const { t } = useTranslation();
  const { showModal } = useContext(ModalContext);
  const { identity } = useContext(IdentityContext);
  const { addToast } = useToast();

  const transfer = licenseTransfers.find(
    transfer => transfer.license.id === license.id && transfer.direction === LICENSE_TRANSFER_DIRECTION.SENDER,
  );
  const isPendingTransfer = Boolean(transfer);

  async function onCancelTransfer() {
    try {
      if (!transfer) return;
      await cancelLicenseTransfer(transfer.id);

      refetchLicenseTransfers();
      addToast({
        id: TOAST_ID.LICENSE_TRANSFER_CANCELLED,
        variant: 'success',
        title: t('TOAST.SUCCESS.TITLE'),
        children: t(`${BASE}.TOASTS.CANCEL_TRANSFER.SUCCESS`),
      });
    } catch (error) {
      addToast({
        id: TOAST_ID.LICENSE_TRANSFER_CANCELLED,
        variant: 'danger',
        title: t('TOAST.DANGER.TITLE'),
        children: t(`ALERTS.RETRY.MESSAGE`),
        actions: buildGroupProps(
          <Button prominence="danger" onClick={() => onCancelTransfer()} key="retry">
            {t(`ALERTS.RETRY.ACTION`)}
          </Button>,
        ),
      });
    }
  }

  if (license.replaced || !identity.isTeacherAlike || getLicenseState(license, licenseTransfers) === 'expired')
    return null;

  return (
    <ButtonGroup
      {...buildGroupProps(
        <>
          {isPendingTransfer ? (
            <Button startIcon={<CloseIcon />} onClick={onCancelTransfer} prominence="neutral" size="sm" key="cancel">
              {t(`${BASE}.CANCEL_TRANSFER`)}
            </Button>
          ) : (
            <Button
              startIcon={<TransferIcon />}
              prominence="neutral"
              size="sm"
              onClick={() =>
                showModal(
                  <LicenseTransferModal
                    id="license-transfer-modal"
                    license={license}
                    onConfirm={refetchLicenseTransfers}
                  />,
                )
              }
              key="start"
            >
              {t(`${BASE}.START_TRANSFER`)}
            </Button>
          )}
        </>,
      )}
      size="sm"
    />
  );
}
